import React, { useState } from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { useWebSocketMeasurementContext } from '@shared/providers/WebSocketMeasurementProvider';

const formatTime = (ts: number) => {
  const d = new Date(ts);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}.${d.getMilliseconds().toString().padStart(3, '0')}`;
};

export const WebSocketDebugOverlay: React.FC = () => {
  const [collapsed, setCollapsed] = useState(true);
  const { rawMessages, status, isConnected } = useWebSocketMeasurementContext();

  if (collapsed) {
    return (
      <TouchableOpacity style={styles.badge} onPress={() => setCollapsed(false)}>
        <View style={[styles.dot, { backgroundColor: isConnected ? '#22c55e' : '#ef4444' }]} />
        <Text style={styles.badgeText}>WS ({rawMessages.length})</Text>
      </TouchableOpacity>
    );
  }

  return (
    <View style={styles.panel} pointerEvents="box-none">
      <View style={styles.header}>
        <View style={[styles.dot, { backgroundColor: isConnected ? '#22c55e' : '#ef4444' }]} />
        <Text style={styles.headerText}>WS: {status}</Text>
        <TouchableOpacity onPress={() => setCollapsed(true)}>
          <Text style={styles.closeText}>✕</Text>
        </TouchableOpacity>
      </View>
      <ScrollView style={styles.scrollView}>
        {rawMessages.length === 0 && (
          <Text style={styles.empty}>No messages yet</Text>
        )}
        {rawMessages.map((msg) => (
          <View key={msg.id} style={styles.messageRow}>
            <Text style={styles.messageMeta}>
              {formatTime(msg.ts)} {msg.direction === 'in' ? '⬇ IN' : '⬆ OUT'}
            </Text>
            <Text style={styles.messageBody}>
              {msg.json ? JSON.stringify(msg.json, null, 1) : msg.raw}
            </Text>
          </View>
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  badge: {
    position: 'absolute',
    bottom: 90,
    right: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.75)',
    borderRadius: 16,
    paddingVertical: 6,
    paddingHorizontal: 10,
    zIndex: 9999,
    elevation: 9999,
  },
  badgeText: {
    color: 'white',
    fontSize: 11,
    fontFamily: 'monospace',
    marginLeft: 6,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  panel: {
    position: 'absolute',
    top: 60,
    left: 8,
    right: 8,
    bottom: 90,
    backgroundColor: 'rgba(10,10,10,0.92)',
    borderRadius: 10,
    zIndex: 9999,
    elevation: 9999,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.15)',
  },
  headerText: {
    color: 'white',
    fontFamily: 'monospace',
    fontSize: 12,
    marginLeft: 8,
    flex: 1,
  },
  closeText: {
    color: 'white',
    fontSize: 16,
    paddingHorizontal: 4,
  },
  scrollView: {
    flex: 1,
    padding: 8,
  },
  empty: {
    color: 'rgba(255,255,255,0.5)',
    fontFamily: 'monospace',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 20,
  },
  messageRow: {
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
    paddingBottom: 8,
  },
  messageMeta: {
    color: '#7dd3fc',
    fontFamily: 'monospace',
    fontSize: 10,
    marginBottom: 2,
  },
  messageBody: {
    color: '#e5e7eb',
    fontFamily: 'monospace',
    fontSize: 10,
  },
});

export default WebSocketDebugOverlay;
